import { useCallback, useEffect, useRef, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  fetchStorageStats,
  getUrl,
  listStorageProviders,
  setDefaultStorageProvider,
  setStorageProviderActive,
  STORAGE_PROVIDERS,
  upload,
  type StorageProviderRow,
} from '@/lib/coreStorage';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const METHOD_DOCS = [
  { key: 'upload(file, folder)', label: 'Envoyer un fichier', icon: 'ri-upload-cloud-2-line' },
  { key: 'download(key)', label: 'Télécharger un fichier', icon: 'ri-download-2-line' },
  { key: 'delete(key)', label: 'Supprimer un fichier', icon: 'ri-delete-bin-6-line' },
  { key: 'getUrl(key)', label: 'Obtenir l’URL', icon: 'ri-link' },
];

export default function StorageTab({ org }: Props) {
  const [providers, setProviders] = useState<StorageProviderRow[]>([]);
  const [stats, setStats] = useState({ providerCount: 0, activeCount: 0, defaultProvider: 'seaweedfs' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<string>('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, s] = await Promise.all([listStorageProviders(org.id), fetchStorageStats(org.id)]);
      setProviders(p);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const makeDefault = async (row: StorageProviderRow) => {
    setBusyId(row.id);
    try { await setDefaultStorageProvider(row.id, row.organization_id); load(); } finally { setBusyId(null); }
  };

  const toggleActive = async (row: StorageProviderRow) => {
    setBusyId(row.id);
    try { await setStorageProviderActive(row.id, !(row.is_active !== false)); load(); } finally { setBusyId(null); }
  };

  const doUpload = async (file: File) => {
    setUploading(true);
    setUploadResult('');
    try {
      const obj = await upload(file, `org-${org.id}/storage-test`, { organizationId: org.id });
      const url = await getUrl(obj.key, { organizationId: org.id });
      setUploadResult(`Fichier envoyé via « ${obj.provider} » : ${obj.key} (${url ? 'URL disponible' : 'URL indisponible'})`);
    } catch (e) {
      setUploadResult(`Échec : ${e instanceof Error ? e.message : 'erreur inconnue'}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Abstraction de stockage</span> : Zifek Core ne dépend d’aucun stockage local particulier.
        Les modules utilisent toujours <code className="bg-background-100 px-1 rounded">upload()</code>, <code className="bg-background-100 px-1 rounded">download()</code>,
        <code className="bg-background-100 px-1 rounded mx-1">delete()</code> et <code className="bg-background-100 px-1 rounded">getUrl()</code> — sans connaître
        le fournisseur (SeaweedFS, S3 compatible, stockage cloud, ou un stockage futur).
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-hard-drive-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.providerCount}</p>
          <p className="text-xs text-foreground-500">Fournisseurs</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.activeCount}</p>
          <p className="text-xs text-foreground-500">Actifs</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-3">
          <i className="ri-star-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1 truncate">{stats.defaultProvider}</p>
          <p className="text-xs text-foreground-500">Par défaut</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-database-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Fournisseurs de stockage</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : (
          <div className="divide-y divide-background-200/60">
            {providers.map((p) => {
              const impl = STORAGE_PROVIDERS[p.provider_key];
              return (
                <div key={p.id} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                    <i className={p.kind === 's3' ? 'ri-server-line' : p.kind === 'cloud' ? 'ri-cloud-line' : 'ri-hard-drive-2-line'}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-800 truncate flex items-center gap-2">
                      {p.label || p.provider_key}
                      {p.is_default && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900">par défaut</span>}
                      {!impl && <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">non branché</span>}
                    </p>
                    <p className="text-[11px] text-foreground-400 truncate">
                      <code className="bg-background-100 px-1 rounded">{p.provider_key}</code> · {p.kind} · priorité {p.priority ?? '—'}
                      {p.organization_id == null ? ' · plateforme' : ' · entreprise'}
                    </p>
                  </div>
                  <button onClick={() => toggleActive(p)} disabled={busyId === p.id} title={p.is_active !== false ? 'Désactiver' : 'Activer'} className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${p.is_active !== false ? 'bg-primary-500' : 'bg-background-200'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${p.is_active !== false ? 'left-[22px]' : 'left-0.5'}`}></span>
                  </button>
                  {!p.is_default && impl && (
                    <button onClick={() => makeDefault(p)} disabled={busyId === p.id} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-[11px] font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">
                      Définir par défaut
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-code-s-slash-line text-accent-600"></i>API agnostique</h4>
          <div className="space-y-2">
            {METHOD_DOCS.map((m) => (
              <div key={m.key} className="flex items-center gap-2 text-xs">
                <span className="w-6 h-6 rounded bg-background-100 text-foreground-600 flex items-center justify-center"><i className={m.icon}></i></span>
                <code className="text-foreground-700">{m.key}</code>
                <span className="text-foreground-400 ml-auto">{m.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-upload-cloud-2-line text-primary-600"></i>Tester l’envoi</h4>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) doUpload(f); }} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>} Choisir un fichier
          </button>
          {uploadResult && <p className="mt-3 text-[11px] text-foreground-600 break-words">{uploadResult}</p>}
          <p className="mt-3 text-[11px] text-foreground-400">Le module ignore le fournisseur utilisé : il appelle simplement <code className="bg-background-100 px-1 rounded">upload()</code>.</p>
        </div>
      </div>
    </div>
  );
}